import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import CustomDatePicker from "../../../../../components/datepicker/CustomDatePicker";
import FormItem from "../../../../../components/form/FormItem";
import { DAY_DATE_FORMAT } from "../../../../../configs/constants";
import { selectNewOrder } from "../../store/orderSlice";
import ServiceType from "./components/ServiceType";
import "./ServiceStep.scss";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import companySettingsApi from "../../../../client/settings/api/companySettingsApi";
import { selectGroup } from "modules/auth/store/authSlice";
import { UserGroup } from "configs/constants";
import useAssignableDrivers from "modules/shared/driver-assign/useAssignableDrivers";

dayjs.extend(utc);
dayjs.extend(timezone);

const ServiceStep = ({
  flexioServices,
  routes,
  setRoutes,
  pickupDate,
  setPickupDate,
  estimatePriceOrganization,
  clientId // Add clientId prop to fetch correct client settings
}) => {
  const newOrder = useSelector(selectNewOrder);
  const [sortedSelectServices, setSortedSelectedServices] = useState(flexioServices);
  const [routeVisibility, setRouteVisibility] = useState("all");
  const isAdmin = useSelector(selectGroup) === UserGroup.FLEXIO;
  // Loaded once here rather than per service card
  const { drivers, loading: driversLoading } = useAssignableDrivers(
    isAdmin,
    true,
    clientId
  );

  // 2025-05-12: Must expressly use startOf("day") everywhere to avoid timezone issues
  // 2026-02-12: Validate localStorage date against 4PM PR cutoff before setting parent state.
  // Previously, a stale localStorage date could be set on the parent while the DatePicker
  // internally corrected itself, causing the backend to receive the stale date.
  useEffect(() => {
    const savedDateStr = localStorage.getItem("pickupDate");
    if (savedDateStr && setPickupDate) {
      const savedDate = dayjs(savedDateStr).startOf("day");
      const now = dayjs().tz("America/Puerto_Rico");
      const todayAt16PM = now.set("hour", 16).set("minute", 0).set("second", 0);
      const minDate = now.isBefore(todayAt16PM)
        ? now.startOf("day")
        : now.add(1, "day").startOf("day");

      if (savedDate.isBefore(minDate)) {
        const tomorrow = dayjs().add(1, "day").startOf("day");
        setPickupDate(tomorrow);
        localStorage.setItem("pickupDate", tomorrow.format("YYYY-MM-DD"));
      } else {
        setPickupDate(savedDate);
      }
    } else {
      const tomorrow = dayjs().add(1, "day").startOf("day");
      setPickupDate(tomorrow);
      localStorage.setItem("pickupDate", tomorrow.format("YYYY-MM-DD"));
    }
  }, [setPickupDate]);

  useEffect(() => {
    setSortedSelectedServices(flexioServices);
  }, [flexioServices]);

  // Fetch client's default route visibility
  useEffect(() => {
    const fetchDefaultVisibility = async () => {
      try {
        // If clientId is provided (admin mode), fetch settings for that specific client
        // Otherwise fetch settings for the logged-in user's client
        let settings;
        if (clientId) {
          // Admin mode: fetch settings for the selected client
          settings = await companySettingsApi.getClientSettings(clientId);
        } else {
          // Client mode: fetch settings for logged-in user's client
          settings = await companySettingsApi.getClientSettings();
        }
        console.log("Fetched client settings:", settings);
        if (settings && settings.defaultRouteVisibility) {
          console.log("Setting route visibility to:", settings.defaultRouteVisibility);
          setRouteVisibility(settings.defaultRouteVisibility);
        } else {
          console.log("No defaultRouteVisibility in settings, using 'all'");
        }
      } catch (error) {
        console.log("Could not fetch default route visibility, using 'all':", error);
      }
    };

    fetchDefaultVisibility();
  }, [clientId]);

  // Update all routes when visibility changes
  useEffect(() => {
    console.log("[FRONTEND ServiceStep] Route visibility changed to:", routeVisibility);
    if (routes && Object.keys(routes).length > 0) {
      console.log(
        "[FRONTEND ServiceStep] Updating routes with visibility:",
        routeVisibility
      );
      const updatedRoutes = { ...routes };
      Object.keys(updatedRoutes).forEach((key) => {
        updatedRoutes[key] = {
          ...updatedRoutes[key],
          visibility: routeVisibility
        };
        console.log(
          `[FRONTEND ServiceStep] Route ${key} now has visibility:`,
          updatedRoutes[key].visibility
        );
      });
      setRoutes(updatedRoutes);
    }
  }, [routeVisibility]);

  return (
    <div className="service-step-component">
      <div className="title">Select the Best Service for Your Route</div>
      <div className="sub-title" style={{ fontSize: "13px", marginBottom: "12px" }}>
        Streamline deliveries with Flexio&apos;s smart route optimization, saving time and
        cutting costs.
      </div>

      <div style={{ display: "flex", gap: "16px", marginBottom: "12px" }}>
        <FormItem label="Pickup Date" className="pickup-date-item" style={{ flex: 1 }}>
          <CustomDatePicker
            format={DAY_DATE_FORMAT}
            onChange={(val) => setPickupDate(val)}
          />
        </FormItem>

        <FormItem label="Select Route Visibility" style={{ flex: 1 }}>
          <div
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              padding: "12px",
              background: "#fafafa"
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "2px"
                }}
              >
                <input
                  type="radio"
                  name="routeVisibility"
                  value="all"
                  checked={routeVisibility === "all"}
                  onChange={(e) => setRouteVisibility(e.target.value)}
                  style={{ marginRight: "8px" }}
                />
                <span>All - open</span>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "2px"
                }}
              >
                <input
                  type="radio"
                  name="routeVisibility"
                  value="internal_only"
                  checked={routeVisibility === "internal_only"}
                  onChange={(e) => setRouteVisibility(e.target.value)}
                  style={{ marginRight: "8px" }}
                />
                <span>Internal Drivers only</span>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "2px"
                }}
              >
                <input
                  type="radio"
                  name="routeVisibility"
                  value="manual_only"
                  checked={routeVisibility === "manual_only"}
                  onChange={(e) => setRouteVisibility(e.target.value)}
                  style={{ marginRight: "8px" }}
                />
                <span>Manual assign only</span>
              </label>
            </div>
          </div>
        </FormItem>
      </div>

      {newOrder?.isConfirmRoute && (
        <div className="wrap-plan-service" style={{ marginTop: "8px" }}>
          {sortedSelectServices.map((service) => (
            <ServiceType
              key={service.id}
              service={service}
              routes={routes}
              setRoutes={setRoutes}
              estimatePriceOrganization={estimatePriceOrganization}
              drivers={drivers}
              driversLoading={driversLoading}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceStep;
